/**
 * Where the self-hosted MathJax assets live.
 *
 * Imported by BOTH `MathJaxProvider` and `vite.config.ts`, because the URLs the
 * browser requests and the directories the build copies into are the same
 * strings. Spelling them twice is a contract that drifts silently: the app 404s
 * the engine while every type-check, lint and unit test stays green.
 *
 * That shared use is also why this module imports nothing and avoids the `@/`
 * alias. Vite bundles its own config before evaluating it, and the alias does
 * not exist at that point — the config is what defines it — so anything the
 * config reaches can only use relative, dependency-free code. Adding an import
 * here breaks `vite build`, `vitest` and Storybook at once, since all three
 * load this config.
 */

/** Holds the engine — below the site root at runtime, below `dist/` at build. */
export const MATHJAX_DIR = 'mathjax';

/**
 * The one combined bundle the provider loads, of the twelve MathJax ships.
 *
 * **SVG output, not CHTML, and the report export is the reason.** CHTML draws
 * an expression as `<span>`s positioned with CSS, which is the better-looking
 * option on a page and cannot leave one: Typst reads SVG, PNG and JPEG, so a
 * CHTML expression can reach the PDF only through a second math implementation
 * that the first can then disagree with. SVG output serialises to something
 * both renderers embed — `#image()` in Typst, `<img>` or inline in the page —
 * so one engine serves both.
 *
 * Loading both is not the escape: MathJax is a browser singleton, which is why
 * `MathJaxProvider` says to mount exactly one context. It is one output format
 * per app.
 *
 * What SVG costs on the page: glyph outlines rather than webfont text, so the
 * expression does not inherit the surrounding font and is not selectable as
 * characters. It scales cleanly and carries its own glyphs, which is the same
 * property that makes it exportable.
 */
export const MATHJAX_BUNDLE = 'tex-mml-svg.js';

/** What `MathJaxContext` is given as `src`, instead of its jsDelivr default. */
export const MATHJAX_SRC = `/${MATHJAX_DIR}/${MATHJAX_BUNDLE}`;

/**
 * MathJax's `[fonts]` path variable. It appends `<font>-font/...` itself, so
 * this names the directory that holds font packages, not a font.
 */
export const MATHJAX_FONTS_PATH = `/${MATHJAX_DIR}/fonts`;

/** `viteStaticCopy` dest for the font package, relative to `dist/`. */
export const MATHJAX_FONT_DEST = `${MATHJAX_FONTS_PATH.slice(1)}/mathjax-newcm-font`;
